import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, User, Menu, X, MapPin, Phone } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import api from "@/lib/api";

export const StoreHeader = () => {
  const { count } = useCart();
  const { user } = useAuth();
  const { settings } = useSettings();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const doSearch = (e) => {
    e.preventDefault();
    nav(`/?search=${encodeURIComponent(q)}`);
    setOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-[#FBF9F5]/90 border-b border-stone-200">
      <div className="bg-[#1E3A2B] text-[#FEF3C7] text-xs">
        <div className="max-w-7xl mx-auto px-4 py-1.5 flex items-center justify-between">
          <span className="flex items-center gap-1"><MapPin size={12} /> {settings.address || "ধড়মোকাম, শেরপুর, বগুড়া"}</span>
          <a href={`tel:${settings.phone || ""}`} className="hidden sm:flex items-center gap-1 hover:text-white"><Phone size={12} /> {settings.phone || ""}</a>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3">
        <Link to="/" data-testid="store-logo" className="flex items-center gap-2 shrink-0">
          {settings.logo ? (
            <img src={settings.logo} alt="logo" className="w-10 h-10 rounded-xl object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-[#1E3A2B] flex items-center justify-center text-[#CA8A04] font-bold text-sm">MD</div>
          )}
          <div className="hidden sm:block leading-tight">
            <div className="font-bold text-[#1E3A2B] text-base">{settings.business_name || "M.D.Rice Mill"}</div>
            <div className="text-[10px] text-stone-500 font-latin">RICE MILL & STORE</div>
          </div>
        </Link>

        <form onSubmit={doSearch} className="flex-1 max-w-xl relative hidden sm:block">
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="চাল, ধান, খুদ খুঁজুন..."
            className="w-full h-11 pl-10 pr-4 rounded-xl border border-stone-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3A2B]/30"
          />
          <Search size={18} className="absolute left-3 top-3 text-stone-400" />
        </form>

        <div className="flex items-center gap-2 ml-auto">
          <Link
            to={user ? "/account" : "/login"}
            data-testid="account-link"
            className="hidden sm:flex items-center gap-1.5 h-11 px-3 rounded-xl border border-stone-300 bg-white hover:bg-stone-50 text-sm font-medium"
          >
            <User size={18} /> {user ? user.name.split(" ")[0] : "লগইন"}
          </Link>
          <Link
            to="/cart"
            data-testid="cart-link"
            className="relative flex items-center justify-center w-11 h-11 rounded-xl bg-[#1E3A2B] text-white"
          >
            <ShoppingCart size={20} />
            {count > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-[#CA8A04] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {count}
              </span>
            )}
          </Link>
          <button className="sm:hidden w-11 h-11 flex items-center justify-center rounded-xl border border-stone-300 bg-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="sm:hidden border-t border-stone-200 bg-white px-4 py-3 space-y-3">
          <form onSubmit={doSearch} className="relative">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="খুঁজুন..." className="w-full h-11 pl-10 pr-4 rounded-xl border border-stone-300" data-testid="mobile-search-input" />
            <Search size={18} className="absolute left-3 top-3 text-stone-400" />
          </form>
          <div className="grid grid-cols-2 gap-2">
            <Link to={user ? "/account" : "/login"} className="h-11 flex items-center justify-center gap-1.5 rounded-xl border border-stone-300 font-medium"><User size={18} /> {user ? "অ্যাকাউন্ট" : "লগইন"}</Link>
            <Link to="/track" className="h-11 flex items-center justify-center rounded-xl border border-stone-300 font-medium">অর্ডার ট্র্যাক</Link>
          </div>
        </div>
      )}

      <nav className="border-t border-stone-200 bg-white/70">
        <div className="max-w-7xl mx-auto px-4 flex items-center gap-1 overflow-x-auto no-scrollbar text-sm">
          {[["সব পণ্য", "/"], ["চাল", "/?category=rice"], ["ধান", "/?category=paddy"], ["খুদ", "/?category=broken"], ["রাইস ব্রান", "/?category=bran"], ["তুষ", "/?category=husk"], ["গুঁড়া", "/?category=powder"], ["অন্যান্য", "/?category=other"], ["অর্ডার ট্র্যাক", "/track"]].map(([label, to]) => (
            <Link key={label} to={to} className="whitespace-nowrap px-3 py-2.5 font-medium text-stone-700 hover:text-[#1E3A2B] border-b-2 border-transparent hover:border-[#CA8A04] transition-colors">
              {label}
            </Link>
          ))}
        </div>
      </nav>
    </header>
  );
};
